import { useState, useEffect, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CardRadioButton,
  DropDown,
  useTranslation,
  Theme,
  MLPVersion,
  useNextgenRouter,
  useFeatureVisibility,
} from '@nextgen-web-framework/all';
import { type AppState, type AppDispatch, StorageService, Logger } from '@nextgen-web-framework/core';
import {
  type SelectedSubscriptionPlan,
  type PlanResponse,
  type Cycle,
  type SubcriptionPlanCMSResponseType,
  updateSelectedSubscription,
  getDefaultPlan,
  updateSubscriptionsCmsData,
  transformSopPlansCMSData,
  updateAutoContinueSop,
} from '@commerce-subscription-webcomponents/sdk';
import { AutoContinueSwitch } from '@commerce-subscription-webcomponents/ui';
import { cmsApi } from '@commerce-webcomponents/sdk';
import HtmlContent from '../HtmlContent/HtmlContent';
import logObject from '../../../logObject';
import { type PlanResponseWithTag, type SubscriptionSelectionPropsTypes, PurchaseTypes } from './types/propTypes';
import SubscriptionDetailsDrawer from './SubscriptionDetailsDrawer/SubscriptionDetailsDrawer';
import FrequencyDetailsDrawer from './FrequencyDetailsDrawer/FrequencyDetailsDrawer';
import styles from './SubscriptionSelection.module.scss';
import SopPlanCards from './SopPlanCards/SopPlanCards';

// Key for manage planId and productId in the session
const selectedPlanAndProductKey = 'sop_ppid';

// eslint-disable-next-line @typescript-eslint/naming-convention
const SubscriptionSelection = memo(function SubscriptionSelection(props: SubscriptionSelectionPropsTypes) {
  const loggerServiceInstance = Logger.getInstance(logObject('SubscriptionSelection'));
  const { subscriptionPlans, showSubscribeButton, purchaseType = PurchaseTypes.ONETIME, productData } = props;
  const { selectedSubscriptionPlan, autoContinueSop } = useSelector((state: AppState) => state.commerceSubscription);
  const displaySopSellableOnly = useFeatureVisibility('product.sopSellableOnly');
  const displayAutoContinue = useFeatureVisibility('subscription.enableAutoContinue');

  const { productId, productFlags } = productData;
  const isSellableForSOPOnly = displaySopSellableOnly ? (productFlags?.isSellableForSOPOnly ?? false) : false;
  const sopPurchaseType = isSellableForSOPOnly ? PurchaseTypes.SUBSCRIPTION : purchaseType;

  const dispatch = useDispatch<AppDispatch>();
  const [selectedOption, setSelectedOption] = useState(sopPurchaseType);
  const [drawer, setDrawer] = useState<boolean>(false);
  const [frequencyDrawer, setFrequencyDrawer] = useState<boolean>(false);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [planList, setPlanList] = useState<PlanResponseWithTag[]>([]);
  const [selectedCMSPlan, setSelectedCMSPlan] = useState({
    shortDescription: '',
    longDescription: '',
    cycleDescription: '',
  });
  const [cmsSopPlans, setCmsSopPlans] = useState<SubcriptionPlanCMSResponseType[]>();

  const localstorage = StorageService.getLocalStorage();
  const sessionStorage = StorageService.getSessionStorage();
  const t = useTranslation();
  const { query, pathname, push, params } = useNextgenRouter();
  const urlContainsSopFlag = query?.get('sop') === 'true';
  const configs = ConfigService.getConfigs();
  const productSopFeature = configs.NEXT_PRODUCT_SOP_FEATURE;
  const sopEnhancedUx = configs.NEXT_SOP_ENHANCED_UX_ENABLED;

  useEffect(() => {
    const shareCode = JSON.parse(localstorage.retrieve('sharecodeId'));
    const subscriptionPlanData = shareCode?.data?.subscriptionPlanData;
    if (subscriptionPlanData?.planId) {
      setSelectedPlan(subscriptionPlanData?.planId);
      setSelectedOption(PurchaseTypes.SUBSCRIPTION);
      dispatch(updateSelectedSubscription(subscriptionPlanData));
      showSubscribeButton(true);
    }

    return () => {
      if (autoContinueSop) {
        handleAutoContinue(false);
      }
    };
  }, []);

  useEffect(() => {
    const validPlans = subscriptionPlans?.plans ? getValidPlans(subscriptionPlans?.plans) : [];
    if (validPlans?.length) {
      type StoredSessionPlanProducts = null | {
        productId: string;
        planId: string;
      };
      const storedPlanAndProduct = (sessionStorage.retrieve(selectedPlanAndProductKey) ||
        null) as StoredSessionPlanProducts;

      // TODO: remove if (productId) cundition and else block once changes are done from product side
      if (productId) {
        if (
          storedPlanAndProduct?.productId == productId &&
          selectedSubscriptionPlan?.planId == storedPlanAndProduct?.planId
        ) {
          checkDefaultPlan(validPlans, selectedSubscriptionPlan?.planId);
        } else {
          checkDefaultPlan(validPlans);
        }
      } else {
        checkDefaultPlan(validPlans, selectedSubscriptionPlan?.planId);
      }

      // Contentstack API call to get the details for all the plans
      getAllPlanCodes(validPlans);
    }
  }, [subscriptionPlans?.plans, params?.locale]);

  const checkDefaultPlan = (validPlans: PlanResponse[], sopPlanId?: string) => {
    const newPlanId = getDefaultPlan(validPlans, sopPlanId)?.id || '';
    // Once change the product/variant resetting default planId
    if (selectedPlan != newPlanId) {
      setSelectedPlan(newPlanId);
    }
  };

  const getValidPlans = (plans: Array<PlanResponse>) => plans.filter((p) => p.restrictPlanOnPdp === false);

  useEffect(() => {
    const selectedPlanObject = planList.find((plan) => plan.id === selectedPlan);
    if (selectedPlanObject) {
      setSelectedCMSPlan({
        shortDescription: selectedPlanObject.shortDescription ?? '',
        longDescription: selectedPlanObject.longDescription ?? '',
        cycleDescription: selectedPlanObject.cycle?.description ?? '',
      });
    } else {
      setSelectedCMSPlan({
        shortDescription: '',
        longDescription: '',
        cycleDescription: '',
      });
    }
  }, [planList, selectedPlan]);

  useEffect(() => {
    if (cmsSopPlans && planList) {
      updateCmsSopPlan(cmsSopPlans);
    }
  }, [cmsSopPlans, selectedPlan, planList]);

  const updateSelectedPlan = (option: PurchaseTypes, planId: string, cycle?: Cycle) => {
    if (option === PurchaseTypes.SUBSCRIPTION) {
      if (planList.length > 0 && planId) {
        dispatchSopPlan(planId, cycle);
      }
      showSubscribeButton(true);
    } else {
      showSubscribeButton(false);
    }
  };

  const getPlanObj = (planId: string) => {
    const plan = planList.filter((plan) => plan.id === planId);
    return plan?.length ? plan[0] : null;
  };

  const dispatchSopPlan = (planId: string, cycle?: Cycle) => {
    const sopPlan = getPlanObj(planId);
    if (!sopPlan?.allowAutoContinue) {
      handleAutoContinue(false);
    }
    dispatch(
      updateSelectedSubscription({
        planId: planId,
        plan: sopPlan,
        cycle: cycle ?? getDefaultPlan(planList, planId)?.cycle,
      } as SelectedSubscriptionPlan),
    );
  };

  const updatePlan = (planId: string, cycle?: Cycle) => {
    setSelectedPlan(planId);
    handleEvent(PurchaseTypes.SUBSCRIPTION, planId, cycle);
  };

  const updateCmsSopPlan = (cmsPlans: SubcriptionPlanCMSResponseType[]) => {
    const planData = planList.find((element) => element.id === selectedPlan);
    const matchingItem = cmsPlans.find((item) => item.plan_code === planData?.code);
    if (matchingItem) {
      dispatch(updateSubscriptionsCmsData([matchingItem]));
    }
  };

  const getAllPlanCodes = async (plans: Array<PlanResponse>) => {
    try {
      const regexConditions = plans?.map((plan) => `{"plan_code":{"$regex":"${plan.code}"}}`);
      const planCodesQuery = `{"$or":[${regexConditions?.join(',')}]}`;

      const res = (await cmsApi.fetchAllContent({
        uId: 'subscription_plan',
        params: [
          {
            key: 'arrayResponse',
            value: 'true',
          },
          {
            key: 'query',
            value: planCodesQuery,
          },
        ],
      })) as SubcriptionPlanCMSResponseType[];
      const cmsPlanResponse = transformSopPlansCMSData(res);

      setCmsSopPlans(cmsPlanResponse);
      const newPlanList = plans?.map((item1) => {
        const matchingItem = cmsPlanResponse.find((item2) => item2.plan_code === item1.code);
        if (matchingItem) {
          return {
            ...item1,
            shortDescription: matchingItem.short_description,
            longDescription: matchingItem.long_description,
            cycle: {
              ...item1.cycle,
              description: matchingItem.frequency_and_length_description,
            },
            planTag: matchingItem.plan_tag,
          };
        }
        return item1;
      });

      setPlanList(newPlanList);
    } catch (err) {
      loggerServiceInstance.log('Error: ', err);
    }
  };

  const frequencyAndLengthText: string = t('subscription.subscriptionSelection.frequencyAndLengthText');

  const updateSopFlag = (option: PurchaseTypes) => {
    const searchParams = new URLSearchParams(window.location.search?.toString());

    if (option === PurchaseTypes.SUBSCRIPTION && !urlContainsSopFlag) {
      searchParams.set('sop', 'true');
      push(`${pathname}?${searchParams.toString()}`, { scroll: false });
    } else if (option === PurchaseTypes.ONETIME && urlContainsSopFlag) {
      searchParams.delete('sop');
      const newUrl = searchParams.toString() ? `${pathname}?${searchParams.toString()}` : pathname;
      push(newUrl as string, { scroll: false });
    }
  };

  const handleEvent = (option: PurchaseTypes, planId: string, cycle?: Cycle) => {
    setSelectedOption(option);
    updateSelectedPlan(option, planId, cycle);
    if (productSopFeature) {
      updateSopFlag(option);
    }
    if (sopEnhancedUx && option == PurchaseTypes.SUBSCRIPTION) {
      sessionStorage.store(selectedPlanAndProductKey, {
        planId,
        productId: productId,
      });
    } else {
      sessionStorage.remove(selectedPlanAndProductKey);
    }
  };

  const handleAutoContinue = (payload: boolean) => {
    dispatch(updateAutoContinueSop(payload));
  };

  useEffect(() => {
    if (urlContainsSopFlag || sopPurchaseType === PurchaseTypes.SUBSCRIPTION) {
      setSelectedOption(PurchaseTypes.SUBSCRIPTION);
      updateSelectedPlan(PurchaseTypes.SUBSCRIPTION, selectedPlan);
    } else {
      setSelectedOption(PurchaseTypes.ONETIME);
      updateSelectedPlan(PurchaseTypes.ONETIME, selectedPlan);
    }
    // productSopFeature && updateSopFlag(purchaseType);
  }, [sopPurchaseType, planList, urlContainsSopFlag]);

  useEffect(() => {
    if (selectedOption == PurchaseTypes.ONETIME && autoContinueSop) {
      handleAutoContinue(false);
    }
  }, [selectedOption]);

  return (
    <>
      {planList.length > 0 && (
        <>
          <div
            className={`${styles['subscription-selection-container']} ${sopEnhancedUx ? styles['gap-32'] : ''}`}
            data-testid="subscription-selection-test-id"
          >
            {!isSellableForSOPOnly && (
              <div className={styles['radio-btn-container']}>
                <label htmlFor="subscription-selection-title" className={styles['title']}>
                  {t('subscription.subscriptionSelection.heading')}
                </label>
                <CardRadioButton
                  id="onetime-purchase-radiobtn"
                  data-testid="onetime-purchase-test-id"
                  label={
                    <div
                      className={sopEnhancedUx && selectedOption === PurchaseTypes.ONETIME ? styles['radio-label'] : ''}
                    >
                      {t('subscription.subscriptionSelection.option1')}
                    </div>
                  }
                  className={sopEnhancedUx && selectedOption === PurchaseTypes.ONETIME ? styles['radio-button'] : ''}
                  checked={selectedOption === PurchaseTypes.ONETIME}
                  onChange={() => {
                    handleEvent(PurchaseTypes.ONETIME, selectedPlan);
                  }}
                  theme={selectedOption === PurchaseTypes.ONETIME ? Theme.PURPLE : Theme.BLACK}
                  mlpVersion={MLPVersion.ONE}
                  divider={!!sopEnhancedUx}
                >
                  {!!sopEnhancedUx && (
                    <div className={styles['one-time-description']}>
                      {t('subscription.subscriptionSelection.oneTimePurchase.description')}
                    </div>
                  )}
                </CardRadioButton>
              </div>
            )}

            <div className={styles['radio-btn-container']}>
              {!!sopEnhancedUx && (
                <label htmlFor="subscription-plans" className={styles['title']}>
                  {t('subscription.subscriptionSelection.sopPlans.heading')}
                </label>
              )}
              {sopEnhancedUx ? (
                <SopPlanCards
                  selectedOption={selectedOption}
                  selectedPlan={selectedPlan}
                  plans={planList}
                  updatePlan={updatePlan}
                />
              ) : (
                <CardRadioButton
                  id="subscribe-save-radiobtn"
                  data-testid="subscribe-save-test-id"
                  label={t('subscription.subscriptionSelection.option2')}
                  checked={selectedOption === PurchaseTypes.SUBSCRIPTION}
                  onChange={() => {
                    handleEvent(PurchaseTypes.SUBSCRIPTION, selectedPlan);
                  }}
                  theme={selectedOption === PurchaseTypes.SUBSCRIPTION ? Theme.PURPLE : Theme.BLACK}
                  divider={true}
                  mlpVersion={MLPVersion.ONE}
                  key={selectedPlan}
                >
                  {selectedPlan && (
                    <div className={styles['plan-short-desc']}>
                      <HtmlContent content={selectedCMSPlan.shortDescription} />
                      {selectedCMSPlan.longDescription && (
                        <div className={styles['link-container']}>
                          <span
                            role="presentation"
                            data-testid="learn-more-test-id"
                            className={styles['learn-more-link']}
                            onClick={() => setDrawer(true)}
                          >
                            {t('subscription.subscriptionSelection.learnMoreText')}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {selectedOption === PurchaseTypes.SUBSCRIPTION && (
                    <div className={styles['plan-options']} data-testid="plan-option-test-id">
                      <div className={styles['frequency-dropdown']}>
                        <DropDown
                          dropdownLabel={frequencyAndLengthText}
                          name="dropdown-frequency"
                          defaultValue={selectedCMSPlan.cycleDescription}
                          value={selectedCMSPlan.cycleDescription}
                          data-testid="frequency-dd-testid"
                          readOnly={true}
                          onClick={() => {
                            setFrequencyDrawer(true);
                          }}
                        />
                      </div>
                    </div>
                  )}
                </CardRadioButton>
              )}
              {displayAutoContinue &&
                selectedSubscriptionPlan?.plan?.allowAutoContinue &&
                selectedOption === PurchaseTypes.SUBSCRIPTION && (
                  <AutoContinueSwitch
                    onClick={() => {
                      handleAutoContinue(!autoContinueSop);
                    }}
                    isSwitchEnabled={autoContinueSop}
                  />
                )}
            </div>
          </div>
          <div className={styles['subscription-drawer-container']}>
            {drawer && (
              <SubscriptionDetailsDrawer showDrawer={setDrawer} longDescription={selectedCMSPlan.longDescription} />
            )}
            {frequencyDrawer && (
              <FrequencyDetailsDrawer
                key={selectedPlan}
                selectedPlan={selectedPlan || ''}
                plans={planList}
                showDrawer={setFrequencyDrawer}
                updatePlan={updatePlan}
              />
            )}
          </div>
        </>
      )}
    </>
  );
});

export default SubscriptionSelection;
